import HotTable, {HotColumn} from "@handsontable/react";
import {ReactElement, ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {LICENSES} from "../../../../constants/Licenses";
import {UnitTypeForPmsActualOverridesUpdateInput, UnitTypeInPlaceRentOverrideInput, useGetInPlaceRentSetupLazyQuery, useSetUnitTypeInPlaceRentOverridesMutation, VersionType} from "../../../../__generated__/generated_types";
import {Property} from "../../../../contexts/properties/PropertiesContext";
import {buildClearOverridesUpdates, buildHOTDataV2, buildMonthlyOverrideValidity, buildPreservePMSValueUpdates, calculateActualsYearAndMonth, calculateTotalMonths, HOTData, mergeUpdates, PendingUpdates} from "./logic";
import {Button, ToggleButton} from "@zendeskgarden/react-buttons";
import Handsontable from "handsontable";
import * as css from "./css.module.scss";
import {GridSettings} from "handsontable/settings";
import {CellRenderer} from "./CellRenderer";
import {ToggleOffOutlined, ToggleOn} from "@material-ui/icons";
import {ReactComponent as InfoIcon} from '@zendeskgarden/svg-icons/src/16/info-fill.svg';
import {Tooltip} from "@zendeskgarden/react-tooltips";

export interface InPlaceRentSetupParams {
    property: Property;
    budgetYear: number;
    customLabelComponent?: ReactNode;
}

export function InPlaceRentSetup(props: InPlaceRentSetupParams): ReactElement | null {
    const [fetchData, {data, loading}] = useGetInPlaceRentSetupLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [saveChanges] = useSetUnitTypeInPlaceRentOverridesMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: () => {
            setPendingUpdates(undefined);
            fetchData({
                variables: {
                    propertyId: props.property.id,
                    pmsDataYears: [props.budgetYear - 2, props.budgetYear - 1, props.budgetYear],
                    budgetYear: props.property.budgetYear
                }
            });
        },
    });
    const [hotData, setHotData] = useState<HOTData>();
    const [hiddenRows, setHiddenRows] = useState<number[]>([]);
    const [pendingUpdates, setPendingUpdates] = useState<PendingUpdates>();
    // invalid override state is where overrides do not follow rule all-or-nothing
    const [invalidForecastedMonthsOverrideState, setInvalidForecastedMonthsOverrideState]
        = useState<Array<boolean>>(new Array<boolean>(calculateTotalMonths(props.property)).fill(false));

    const [isEditing, setIsEditing] = useState(false);
    const hotRef = useRef<HotTable>(null);

    function handleSaveChanges() {
        if(!pendingUpdates) {
            return;
        }
        const overrides: UnitTypeInPlaceRentOverrideInput[] = [];
        const actOverrides: UnitTypeForPmsActualOverridesUpdateInput[] = [];
        const {actualsMonthIndex} = calculateActualsYearAndMonth(props.property);
        for(const [unitTypeId, overrideValues] of Array.from(pendingUpdates.unitTypeUpdates)) {
            const actual = overrideValues[0];
            const reforecast = overrideValues.slice(1, overrideValues.length - 12);
            const budget = overrideValues.slice(overrideValues.length - 12);
            if(actual !== undefined) {
                const actInput: UnitTypeForPmsActualOverridesUpdateInput = {
                    unitTypeId: unitTypeId
                };
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                actInput[`valueM${actualsMonthIndex}`] = actual !== null ? +actual : actual;
                actOverrides.push(actInput);
            }
            if(reforecast.some(v => v !== undefined)) {
                const input: UnitTypeInPlaceRentOverrideInput = {
                    unitTypeId: unitTypeId,
                    versionType: VersionType.Reforecast
                };
                for(let i = 0; i < reforecast.length; i++) {
                    const monthIndex = i + props.property.reforecastStartMonthIndex;
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    input[`valueM${monthIndex}`] = reforecast[i] !== null && reforecast[i] !== undefined ? `${reforecast[i]}` : reforecast[i];
                }
                overrides.push(input);
            }
            if(budget.some(v => v !== undefined)) {
                const input: UnitTypeInPlaceRentOverrideInput = {
                    unitTypeId: unitTypeId,
                    versionType: VersionType.Budget
                };
                for(let i = 0; i < 12; i++) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    input[`valueM${i}`] = budget[i] !== null && budget[i] !== undefined ? `${budget[i]}` : budget[i];
                }
                overrides.push(input);
            }
        }
        if(overrides.length > 0 || actOverrides.length > 0) {
            saveChanges({
                variables: {
                    propertyId: props.property.id,
                    budgetYear: props.property.budgetYear,
                    overrides: overrides,
                    overridesActuals: actOverrides
                }
            });
        }
    }

    function beforeChange(changes: Array<Handsontable.CellChange | null>, source: Handsontable.ChangeSource) {
        if(!changes || source == "loadData" || !hotRef.current || !hotRef.current.hotInstance || !hotData) {
            return;
        }
        const totalMonths = calculateTotalMonths(props.property);
        const updates: PendingUpdates["unitTypeUpdates"] = new Map();
        for(const change of changes) {
            if(change === null) {
                continue;
            }
            let [row, column, oldValue, newValue] = change;
            const col = typeof column === "number" ? column : parseInt(column);
            if(col > 0 && col - 1 < totalMonths) {
                const unitTypeId = hotData.rowNumToUnitTypeId[row];
                if(!unitTypeId) {
                    continue;
                }
                let unitTypeUpdate = updates.get(unitTypeId);
                if(!unitTypeUpdate) {
                    unitTypeUpdate = new Array<number | null | undefined>(totalMonths).fill(undefined);
                    updates.set(unitTypeId, unitTypeUpdate);
                }
                if(newValue == null) {
                    newValue = 0;
                    change[3] = newValue;
                }
                unitTypeUpdate[col - 1] = newValue;
            }
        }
        const merged = mergeUpdates(pendingUpdates?.unitTypeUpdates, updates);
        const monthlyValidity = buildMonthlyOverrideValidity(merged, hotData.calcOverridesByUnitTypeId, props.property);
        setPendingUpdates({
            unitTypeUpdates: merged,
        });
        setInvalidForecastedMonthsOverrideState(monthlyValidity);
    }

    function handleReset() {
        if(!data || loading) {
            return;
        }
        setPendingUpdates(undefined);
        const hotData = buildHOTDataV2(props.property, data);
        const monthlyValidity = buildMonthlyOverrideValidity(new Map(), hotData.calcOverridesByUnitTypeId, props.property);
        setHotData(hotData);
        setInvalidForecastedMonthsOverrideState(monthlyValidity);
        setIsEditing(false);
    }

    function toggleIsEditing() {
        setIsEditing(prev => !prev);
    }

    function handleRowToggleClick(row: number) {
        if(!hotData) {
            return;
        }
        const unitTypeId = hotData.rowNumToUnitTypeId[row];
        if(!unitTypeId) {
            return;
        }
        if(hiddenRows.includes(row + 1)) {
            setHiddenRows(hiddenRows.filter(r => r !== row + 1));
        }
        else {
            setHiddenRows([...hiddenRows, row + 1]);
        }
    }

    function handleAllRowsToggleClick() {
        if(!hotData) {
            return;
        }
        if(hiddenRows.length == 0) {
            const hiddenRows = Object.keys(hotData.rowNumToUnitTypeId).map(row => +row + 1);
            setHiddenRows(hiddenRows);
        }
        else {
            setHiddenRows([]);
        }
    }

    useEffect(() => {
        setHotData(undefined);
        fetchData({
            variables: {
                propertyId: props.property.id,
                pmsDataYears: [props.budgetYear - 2, props.budgetYear - 1, props.budgetYear],
                budgetYear: props.property.budgetYear
            }
        });
    }, [props.property]);

    useEffect(() => {
        if(!data || loading) {
            return;
        }
        const hotData = buildHOTDataV2(props.property, data);
        const hiddenRows = Object.keys(hotData.rowNumToUnitTypeId).map(row => +row + 1);
        const monthlyValidity = buildMonthlyOverrideValidity(new Map(), hotData.calcOverridesByUnitTypeId, props.property);
        setHotData(hotData);
        setInvalidForecastedMonthsOverrideState(monthlyValidity);
        setHiddenRows(hiddenRows);
        setIsEditing(false);
    }, [data, loading]);

    const contextMenu = useMemo(() => {
        const ret: GridSettings["contextMenu"] = {
            items: {
                "preservePMS": {
                    key: "preservePMS",
                    name: "Sync with PMS",
                    callback() {
                        if(!hotData) {
                            return;
                        }
                        const {updates, changes} = buildPreservePMSValueUpdates(hotRef, hotData.rowNumToUnitTypeId, hotData.pmsValuesByUnitTypeId, props.property);
                        if(updates.size > 0) {
                            hotRef.current?.hotInstance?.setDataAtCell(changes, "loadData");
                            const merged = mergeUpdates(pendingUpdates?.unitTypeUpdates, updates);
                            const monthlyValidity = buildMonthlyOverrideValidity(merged, hotData.calcOverridesByUnitTypeId, props.property);
                            setPendingUpdates({unitTypeUpdates: merged});
                            setInvalidForecastedMonthsOverrideState(monthlyValidity);
                        }
                    },
                    disabled() {
                        if(!isEditing) {
                            return true;
                        }
                        if(!hotData) {
                            return true;
                        }
                        const {updates} = buildPreservePMSValueUpdates(hotRef, hotData.rowNumToUnitTypeId, hotData.pmsValuesByUnitTypeId, props.property);
                        return updates.size == 0;
                    }
                },
                "clearOverrides": {
                    key: "clearOverrides",
                    name: "Undo Overrides in Selection",
                    callback() {
                        if(!hotData) {
                            return;
                        }
                        const updates = buildClearOverridesUpdates(
                            props.property,
                            hotRef,
                            hotData.rowNumToUnitTypeId,
                            hotData.calcOverridesByUnitTypeId,
                            hotData.actualOverridesByUnitTypeId,
                            hotData.originalByUnitTypeId
                        );
                        if(updates.updates.size > 0) {
                            hotRef.current?.hotInstance?.setDataAtCell(updates.changes, "loadData");
                            const merged = mergeUpdates(pendingUpdates?.unitTypeUpdates, updates.updates);
                            const monthlyValidity = buildMonthlyOverrideValidity(merged, hotData.calcOverridesByUnitTypeId, props.property);
                            setPendingUpdates({unitTypeUpdates: merged});
                            setInvalidForecastedMonthsOverrideState(monthlyValidity);
                        }
                    },
                    disabled() {
                        if(!isEditing) {
                            return true;
                        }
                        if(!hotData) {
                            return true;
                        }
                        const updates = buildClearOverridesUpdates(
                            props.property,
                            hotRef,
                            hotData.rowNumToUnitTypeId,
                            hotData.calcOverridesByUnitTypeId,
                            hotData.actualOverridesByUnitTypeId,
                            hotData.originalByUnitTypeId
                        );

                        return updates.updates.size == 0;
                    },
                }
            },
        };

        return ret;
    }, [
        pendingUpdates, setPendingUpdates, hotData, isEditing
    ]);

    const hasUpdates = useMemo(() => {
        if(!pendingUpdates) {
            return false;
        }
        return Array.from(pendingUpdates.unitTypeUpdates.values()).some(utUpdates => utUpdates.some(v => v !== undefined));
    }, [pendingUpdates]);

    const hasInvalidMonth = useMemo(() => {
        return invalidForecastedMonthsOverrideState.includes(true);
    }, [invalidForecastedMonthsOverrideState]);

    return (
        <>
            <div className={css.blockHeader}>
                <div>{props.customLabelComponent}</div>
                {hasInvalidMonth &&
                    <Tooltip content="Please ensure that either all unit types have overrides or none have overrides">
                        <div style={{color: "red"}}>Inconsistent overrides for calculated in place rents&nbsp; <InfoIcon /></div>
                    </Tooltip>
                }
                <div className={css.headerActions}>
                    <ToggleButton
                        isBasic
                        onClick={() => toggleIsEditing()}
                    >
                        {isEditing ?
                            <ToggleOn fontSize="large" />
                            : <ToggleOffOutlined fontSize="large" />
                        }
                        <span style={{marginLeft: ".5rem"}}>Edit Mode</span>
                    </ToggleButton>
                    <Button onClick={handleSaveChanges} isPrimary disabled={!isEditing || !hasUpdates || invalidForecastedMonthsOverrideState.some(v => v)}>
                        Save
                    </Button>
                    <Button onClick={handleReset} disabled={!isEditing || !hasUpdates}>
                        Reset
                    </Button>
                </div>
            </div>
            <div className={css.tableWrapper} id="table-wrapper">
                {hotData &&
                    <HotTable
                        ref={hotRef}
                        data={hotData.tableData}
                        colWidths={[200, ...new Array(hotData.colHeaders.length - 1).fill(props.property.isAcquisitionProperty ? 88.48 : 86.4)]}
                        rowHeights={50}
                        contextMenu={contextMenu}
                        readOnly={!isEditing}
                        settings={{
                            className: "htMiddle",
                            manualRowResize: false,
                            manualColumnResize: false,
                            autoColumnSize: false,
                            autoRowSize: false,
                            stretchH: "none",
                            hiddenRows: {
                                rows: hiddenRows,
                                //@ts-expect-error: For some reason our version of handsontable does not provide correct API shape but does support copyPasteEnabled
                                copyPasteEnabled: false
                            },
                            fixedColumnsLeft: 1,
                            fixedRowsTop: 1,
                        }}
                        beforeChange={beforeChange}
                        licenseKey={LICENSES.HandsOnTable}
                        style={{margin: "auto"}}
                    >
                        {hotData.colHeaders.map((col) =>
                            <HotColumn key={col}>
                                <CellRenderer hot-renderer
                                    property={props.property}
                                    rowNumToUnitTypeId={hotData.rowNumToUnitTypeId}
                                    actualOverridesByUnitTypeId={hotData.actualOverridesByUnitTypeId}
                                    calcOverridesByUnitTypeId={hotData.calcOverridesByUnitTypeId}
                                    hotRef={hotRef.current}
                                    invalidForecastedMonthsOverrideState={invalidForecastedMonthsOverrideState}
                                    pendingUpdates={pendingUpdates}
                                    handleRowToggleClick={handleRowToggleClick}
                                    handleAllRowsToggleClick={handleAllRowsToggleClick}
                                    isEditing={isEditing}
                                    allRowsExpanded={hiddenRows.length == 0}
                                    hiddenRows={hiddenRows}
                                />
                            </HotColumn>
                        )}
                    </HotTable>
                }
            </div >
        </>
    );
}